import {useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import {contentGroupOf} from './contentGroups'

declare global {
	interface Window {
		gtag?: (...args: unknown[]) => void
	}
}

export const TITLE_SET_EVENT = 'booksitout:title-set'
export type TitleSetDetail = { pathname: string }

/** 같은 경로에서 제목이 이 시간 동안 안 바뀌면 확정으로 보고 기록 (상세 페이지는 '도서관' 같은 임시 제목 → 로드 후 실제 이름) */
const TITLE_SETTLE_MS = 700
/** 제목이 계속 안 정해지면(useHtmlTitle 미사용, API 느림/실패) 이 시간 후 그대로 기록 */
const TITLE_MAX_WAIT_MS = 3000
const DEFAULT_TITLE = '책잇아웃'

let lastTitledPathname: string | null = null

/** useHtmlTitle이 document.title을 바꾼 직후 호출 */
export const notifyTitleSet = (pathname: string) => {
	lastTitledPathname = pathname
	window.dispatchEvent(new CustomEvent<TitleSetDetail>(TITLE_SET_EVENT, {detail: {pathname}}))
}

const sendPageView = (pathname: string) => {
	window.gtag?.('event', 'page_view', {
		page_title: document.title,
		page_location: window.location.href,
		page_path: pathname,
		content_group: contentGroupOf(pathname),
	})
	if (process.env.NODE_ENV === 'development') {
		console.log(`📊 [Analytics] page_view ${pathname} — ${document.title} (${contentGroupOf(pathname)})`)
	}
}

/**
 * GA page_view를 직접 보낸다 (GA 자동 page_view는 끔: index.html `send_page_view: false` + 스트림의 '브라우저 기록 이벤트 기반 페이지 변경' 끔).
 * 자동 page_view는 URL이 바뀌는 순간 나가서 제목·콘텐츠 그룹이 이전 페이지 값으로 찍히기 때문에,
 * 해당 경로의 제목이 정해진 뒤(useHtmlTitle 후 0.7초 동안 변화 없음, 최대 3초) 한 번만 보낸다.
 * 그 전에 다른 페이지로 떠나면 떠나는 시점에 보낸다.
 */
const usePageViewTracking = () => {
	const location = useLocation()

	useEffect(() => {
		const {pathname} = location
		let sent = false
		let settleTimer: number | undefined
		let maxTimer: number | undefined
		const send = () => {
			if (sent) return
			sent = true
			window.clearTimeout(settleTimer)
			window.clearTimeout(maxTimer)
			sendPageView(pathname)
		}
		const settle = () => {
			window.clearTimeout(settleTimer)
			settleTimer = window.setTimeout(send, TITLE_SETTLE_MS)
		}

		// 자식(페이지)의 useHtmlTitle 효과가 이 효과보다 먼저 돌 수 있다
		if (lastTitledPathname === pathname) {
			settle()
		} else if (lastTitledPathname !== null) {
			document.title = DEFAULT_TITLE
		}

		const onTitleSet = (e: Event) => {
			if ((e as CustomEvent<TitleSetDetail>).detail.pathname === pathname) settle()
		}
		window.addEventListener(TITLE_SET_EVENT, onTitleSet)
		maxTimer = window.setTimeout(send, TITLE_MAX_WAIT_MS)
		return () => {
			window.removeEventListener(TITLE_SET_EVENT, onTitleSet)
			send()
		}
	}, [location.key, location.pathname])
}

export default usePageViewTracking
